/**
 * Haino Teko Germany Air-5 — 60FPS 3D Exploded Teardown Engine
 * Precision Apple AirPods style component teardown & re-assembly.
 * Uses authentic 3D renders of the Haino Teko Air-5:
 *   Frame 0: Open white charging case with levitating earbuds (Haino Teko Germany logo + green LED)
 *   Frame 1: Single assembled Haino Teko Air-5 earbud (extreme macro)
 *   Frame 2: Earbud shell splitting open, revealing acoustic chamber & driver
 *   Frame 3: Full 3D exploded teardown showing all 7 components floating in mid-air
 *   Frame 4: Complete re-assembly into the Matte Black Silicone Armor Case & Lanyard Offer
 */

(function() {
  'use strict';

  class HainoTekoAir5TeardownEngine {
    constructor(container) {
      this.container = container;
      this.canvas = container.querySelector('.apple-scroll-canvas');
      this.ctx = this.canvas ? this.canvas.getContext('2d') : null;
      this.stages = container.querySelectorAll('.apple-scroll-stage');

      this.targetProgress = 0;
      this.currentProgress = 0;
      this.lerpSpeed = 0.085;
      this.rafId = null;

      // Extract frame URLs from data attributes
      this.frameUrls = [
        container.getAttribute('data-frame-0') || container.dataset['frame-0'] || '',
        container.getAttribute('data-frame-1') || container.dataset['frame-1'] || '',
        container.getAttribute('data-frame-2') || container.dataset['frame-2'] || '',
        container.getAttribute('data-frame-3') || container.dataset['frame-3'] || '',
        container.getAttribute('data-frame-4') || container.dataset['frame-4'] || ''
      ];

      this.images = [];
      this.imagesLoaded = 0;
      this.allLoaded = false;
      this.pulseTime = 0;

      this.preloadImages();
      this.initEvents();
    }

    preloadImages() {
      let loadedCount = 0;
      const total = this.frameUrls.length;

      this.frameUrls.forEach((url, idx) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.src = url;
        img.onload = () => {
          loadedCount++;
          this.imagesLoaded = loadedCount;
          if (loadedCount === total) {
            this.allLoaded = true;
          }
          this.render();
        };
        img.onerror = () => {
          console.warn('[HainoTeko Teardown] Failed to load frame ' + idx + ': ' + url);
          loadedCount++;
          if (loadedCount === total) {
            this.allLoaded = true;
          }
        };
        this.images[idx] = img;
      });
    }

    initEvents() {
      const scrollHandler = () => this.onScroll();
      window.addEventListener('scroll', scrollHandler, { passive: true });
      document.addEventListener('scroll', scrollHandler, { passive: true });

      const pw = document.querySelector('.page-wrapper');
      if (pw) {
        pw.addEventListener('scroll', scrollHandler, { passive: true });
      }

      window.addEventListener('resize', () => {
        this.onResize();
        this.render();
      }, { passive: true });

      this.onResize();
      this.onScroll();
      this.startLoop();
    }

    onResize() {
      if (!this.canvas) return;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const w = this.canvas.clientWidth || window.innerWidth;
      const h = this.canvas.clientHeight || window.innerHeight;

      this.canvas.width = w * dpr;
      this.canvas.height = h * dpr;

      if (this.ctx) {
        this.ctx.setTransform(1, 0, 0, 1, 0, 0);
        this.ctx.scale(dpr, dpr);
      }
    }

    onScroll() {
      if (!this.container) return;
      const viewportHeight = window.innerHeight;
      const rect = this.container.getBoundingClientRect();
      const totalScrollable = this.container.offsetHeight - viewportHeight;

      if (totalScrollable <= 0) return;

      const scrolled = -rect.top;
      let progress = scrolled / totalScrollable;
      progress = Math.max(0, Math.min(1, progress));
      this.targetProgress = progress;
    }

    startLoop() {
      if (!this.rafId) {
        this.loop();
      }
    }

    loop() {
      this.pulseTime += 0.035;
      this.onScroll();
      this.currentProgress += (this.targetProgress - this.currentProgress) * this.lerpSpeed;

      this.render();
      this.updateTypography(this.currentProgress);

      this.rafId = requestAnimationFrame(() => this.loop());
    }

    render() {
      if (!this.canvas || !this.ctx) return;
      const ctx = this.ctx;
      const cw = this.canvas.clientWidth || window.innerWidth;
      const ch = this.canvas.clientHeight || window.innerHeight;

      ctx.clearRect(0, 0, cw, ch);

      // Deep luxury black canvas background
      ctx.fillStyle = '#050505';
      ctx.fillRect(0, 0, cw, ch);

      // Ambient radial cyan & emerald aura
      const cx = cw / 2;
      const cy = ch / 2;
      const glowGrad = ctx.createRadialGradient(cx, cy, 30, cx, cy, Math.max(cw, ch) * 0.55);
      glowGrad.addColorStop(0, 'rgba(0, 229, 255, 0.08)');
      glowGrad.addColorStop(0.5, 'rgba(0, 230, 118, 0.02)');
      glowGrad.addColorStop(1, 'rgba(5, 5, 5, 0)');
      ctx.fillStyle = glowGrad;
      ctx.fillRect(0, 0, cw, ch);

      const p = this.currentProgress;

      // Render 5 Keyframe Haino Teko Air-5 stages
      // Anchors:
      // k0 = 0.08: Case open, earbuds levitating
      // k1 = 0.28: Assembled earbud macro
      // k2 = 0.50: Earbud shell opening (partial teardown)
      // k3 = 0.72: Full 3D exploded teardown + HUD pins
      // k4 = 0.92: Silicone armor case + lanyard bundle offer

      const k0 = 0.08;
      const k1 = 0.28;
      const k2 = 0.50;
      const k3 = 0.72;
      const k4 = 0.92;

      let imgA = null, imgB = null;
      let alphaA = 1, alphaB = 0;
      let scaleA = 1, scaleB = 1;
      let showHUD = false;
      let hudAlpha = 0;

      if (p <= k0) {
        imgA = this.images[0];
        alphaA = 1;
        scaleA = 1.0 + (p / k0) * 0.04;
      } else if (p > k0 && p <= k1) {
        const t = (p - k0) / (k1 - k0);
        imgA = this.images[0];
        imgB = this.images[1];
        alphaA = 1 - t;
        alphaB = t;
        scaleA = 1.04 + t * 0.06;
        scaleB = 0.94 + t * 0.06;
      } else if (p > k1 && p <= k2) {
        const t = (p - k1) / (k2 - k1);
        imgA = this.images[1];
        imgB = this.images[2];
        alphaA = 1 - t;
        alphaB = t;
        scaleA = 1.0 + t * 0.05;
        scaleB = 0.96 + t * 0.04;
      } else if (p > k2 && p <= k3) {
        const t = (p - k2) / (k3 - k2);
        imgA = this.images[2];
        imgB = this.images[3];
        alphaA = 1 - t;
        alphaB = t;
        scaleA = 1.01 + t * 0.04;
        scaleB = 0.97 + t * 0.03;
        if (t > 0.3) {
          showHUD = true;
          hudAlpha = (t - 0.3) / 0.7;
        }
      } else if (p > k3 && p <= k4) {
        const t = (p - k3) / (k4 - k3);
        imgA = this.images[3];
        imgB = this.images[4];
        alphaA = 1 - t;
        alphaB = t;
        scaleA = 1.0 - t * 0.04;
        scaleB = 0.95 + t * 0.05;
        if (t < 0.6) {
          showHUD = true;
          hudAlpha = 1 - (t / 0.6);
        }
      } else {
        imgA = this.images[4];
        alphaA = 1;
        scaleA = 1.0 + ((p - k4) / (1 - k4)) * 0.04;
      }

      // Draw primary image
      if (imgA && imgA.complete && imgA.naturalWidth > 0 && alphaA > 0.01) {
        this.drawImageContained(ctx, imgA, alphaA, scaleA, cw, ch);
      }

      // Draw secondary image (crossfade)
      if (imgB && imgB.complete && imgB.naturalWidth > 0 && alphaB > 0.01) {
        this.drawImageContained(ctx, imgB, alphaB, scaleB, cw, ch);
      }

      // Draw Interactive Glowing HUD Callouts when exploded
      if (showHUD && hudAlpha > 0.02) {
        this.drawTeardownHUD(ctx, cw, ch, hudAlpha);
      }

      // Draw Offer Badges when in bundle stage
      if (p >= 0.82) {
        const badgeAlpha = Math.min(1, (p - 0.82) / 0.10);
        this.drawBundleBadges(ctx, cw, ch, badgeAlpha);
      }
    }

    drawImageContained(ctx, img, alpha, scale, cw, ch) {
      ctx.save();
      ctx.globalAlpha = Math.max(0, Math.min(1, alpha));

      const imgW = img.naturalWidth || 1920;
      const imgH = img.naturalHeight || 1080;
      const imgAspect = imgW / imgH;
      const screenAspect = cw / ch;

      let drawW, drawH;
      if (screenAspect > imgAspect) {
        // Desktop (wider)
        drawH = ch * 0.88 * scale;
        drawW = drawH * imgAspect;
      } else {
        // Mobile (taller/narrower) — scale nicely so the product is prominent and completely fills center
        drawW = cw * 1.04 * scale;
        drawH = drawW / imgAspect;
      }

      const dx = (cw - drawW) / 2;
      const dy = (ch - drawH) / 2 - (ch < 768 ? 45 : 25);

      ctx.drawImage(img, dx, dy, drawW, drawH);
      ctx.restore();
    }

    drawTeardownHUD(ctx, cw, ch, alpha) {
      ctx.save();
      ctx.globalAlpha = alpha;

      const isMobile = cw < 768;
      const cy = ch / 2 - (isMobile ? 40 : 25);

      // Estimate the bounds of the exploded teardown image
      const imgAspect = 16 / 9;
      let drawW, drawH;
      if (cw / ch > imgAspect) {
        drawH = ch * 0.88;
        drawW = drawH * imgAspect;
      } else {
        drawW = cw * 1.04;
        drawH = drawW / imgAspect;
      }

      const leftX = (cw - drawW) / 2;
      const topY = (ch - drawH) / 2 - (isMobile ? 45 : 25);

      // Central cyan dashed laser alignment axis
      ctx.strokeStyle = 'rgba(0, 229, 255, 0.4)';
      ctx.setLineDash([6, 6]);
      ctx.lineDashOffset = -this.pulseTime * 15;
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(leftX + drawW * 0.06, cy);
      ctx.lineTo(leftX + drawW * 0.94, cy);
      ctx.stroke();
      ctx.setLineDash([]);

      // Component Pin Definitions mapped to exact 3D render coordinates
      // Each pin: { relX: 0-1, relY: 0-1, title: '', sub: '', isTop: bool, mobileSkip: bool }
      const pins = [
        {
          relX: 0.13,
          relY: 0.50,
          title: 'Silicone Ear Tip',
          sub: 'Acoustic Seal & Bass Lock',
          isTop: true,
          mobileSkip: false
        },
        {
          relX: 0.26,
          relY: 0.50,
          title: 'Acoustic Nozzle',
          sub: 'Micro-Mesh Dust & Sweat Shield',
          isTop: false,
          mobileSkip: true
        },
        {
          relX: 0.38,
          relY: 0.50,
          title: '13mm Titanium Diaphragm',
          sub: 'Dynamic Bass Motor',
          isTop: false,
          mobileSkip: false
        },
        {
          relX: 0.47,
          relY: 0.50,
          title: 'Copper Voice Coil',
          sub: 'Zero Distortion Audio Response',
          isTop: false,
          mobileSkip: true
        },
        {
          relX: 0.57,
          relY: 0.50,
          title: 'Apple Pop-Up SoC',
          sub: 'BT 5.3 + Live iOS Battery Widget',
          isTop: true,
          mobileSkip: false
        },
        {
          relX: 0.67,
          relY: 0.50,
          title: 'Lithium Battery Cell',
          sub: '24h Total Power with Fast Charge',
          isTop: false,
          mobileSkip: false
        },
        {
          relX: 0.82,
          relY: 0.50,
          title: 'Ergonomic Housing',
          sub: 'Capacitive Touch Controls',
          isTop: true,
          mobileSkip: true
        }
      ];

      const pulse = (Math.sin(this.pulseTime * 4) + 1) / 2; // 0 to 1

      pins.forEach((pin, i) => {
        if (isMobile && pin.mobileSkip) return;

        const px = leftX + drawW * pin.relX;
        const py = topY + drawH * pin.relY;

        // Pulsing Neon Target Ring
        ctx.save();
        ctx.strokeStyle = '#00E5FF';
        ctx.lineWidth = 1.5;
        ctx.shadowColor = '#00E5FF';
        ctx.shadowBlur = 12;

        ctx.beginPath();
        ctx.arc(px, py, 4 + pulse * 4, 0, Math.PI * 2);
        ctx.stroke();

        ctx.fillStyle = '#00E5FF';
        ctx.beginPath();
        ctx.arc(px, py, 2.5, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();

        // Callout Line & Label
        const isPinTop = isMobile ? (pin.relX === 0.13 || pin.relX === 0.57) : pin.isTop;
        const lineLen = isMobile ? (isPinTop ? -38 : 38) : (pin.isTop ? -75 : 75);
        // On mobile, keep text inside by directing left pins inward to the right
        const goRight = isMobile ? (pin.relX < 0.45 || pin.relX > 0.65) : (pin.relX > 0.5);
        const elbowOffset = goRight ? (isMobile ? 18 : 22) : (isMobile ? -18 : -22);
        const elbowX = px + elbowOffset;
        const elbowY = py + lineLen;
        const textEndOffset = goRight ? (isMobile ? 52 : 82) : (isMobile ? -52 : -82);
        const textEndX = elbowX + textEndOffset;

        ctx.save();
        ctx.strokeStyle = 'rgba(0, 229, 255, 0.75)';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(px, py);
        ctx.lineTo(elbowX, elbowY);
        ctx.lineTo(textEndX, elbowY);
        ctx.stroke();

        // Label Typography
        ctx.font = isMobile ? '700 8.5px -apple-system, Inter, sans-serif' : '700 11px -apple-system, Inter, sans-serif';
        ctx.fillStyle = '#FFFFFF';
        ctx.textAlign = goRight ? 'left' : 'right';
        ctx.shadowColor = 'rgba(0,0,0,0.9)';
        ctx.shadowBlur = 5;
        ctx.fillText(pin.title, elbowX + (goRight ? 4 : -4), elbowY - 3.5);

        ctx.font = isMobile ? '500 7px -apple-system, Inter, sans-serif' : '500 8.5px -apple-system, Inter, sans-serif';
        ctx.fillStyle = '#00E5FF';
        ctx.fillText(pin.sub, elbowX + (goRight ? 4 : -4), elbowY + 8);

        ctx.restore();
      });

      ctx.restore();
    }

    drawBundleBadges(ctx, cw, ch, alpha) {
      ctx.save();
      ctx.globalAlpha = alpha;

      const isMobile = cw < 768;
      const cy = ch / 2 - (isMobile ? 40 : 25);

      const badges = [
        { text: '🎁 FREE SILICONE ARMOR CASE', color: '#00E5FF', dx: isMobile ? -80 : -160, dy: isMobile ? -110 : -140 },
        { text: '🧵 FREE ANTI-LOST LANYARD', color: '#00E676', dx: isMobile ? 80 : 160, dy: isMobile ? -110 : -140 }
      ];

      badges.forEach(b => {
        ctx.save();
        ctx.translate(cw / 2 + b.dx, cy + b.dy);

        const badgeW = isMobile ? 140 : 180;
        const badgeH = isMobile ? 28 : 34;

        ctx.fillStyle = 'rgba(10, 10, 10, 0.88)';
        ctx.strokeStyle = b.color;
        ctx.lineWidth = 1.5;
        ctx.shadowColor = b.color;
        ctx.shadowBlur = 12;

        this.roundRect(ctx, -badgeW / 2, -badgeH / 2, badgeW, badgeH, badgeH / 2);
        ctx.fill();
        ctx.stroke();

        ctx.fillStyle = b.color;
        ctx.font = isMobile ? '800 8.5px -apple-system, Inter, sans-serif' : '800 10px -apple-system, Inter, sans-serif';
        ctx.textAlign = 'center';
        ctx.shadowBlur = 0;
        ctx.fillText(b.text, 0, isMobile ? 3 : 3.5);

        ctx.restore();
      });

      ctx.restore();
    }

    roundRect(ctx, x, y, width, height, radius) {
      if (typeof radius === 'number') {
        radius = { tl: radius, tr: radius, br: radius, bl: radius };
      } else {
        radius = { tl: 0, tr: 0, br: 0, bl: 0, ...radius };
      }
      ctx.beginPath();
      ctx.moveTo(x + radius.tl, y);
      ctx.lineTo(x + width - radius.tr, y);
      ctx.quadraticCurveTo(x + width, y, x + width, y + radius.tr);
      ctx.lineTo(x + width, y + height - radius.br);
      ctx.quadraticCurveTo(x + width, y + height, x + width - radius.br, y + height);
      ctx.lineTo(x + radius.bl, y + height);
      ctx.quadraticCurveTo(x, y + height, x, y + height - radius.bl);
      ctx.lineTo(x, y + radius.tl);
      ctx.quadraticCurveTo(x, y, x + radius.tl, y);
      ctx.closePath();
    }

    updateTypography(p) {
      // 5 Stages:
      // Stage 0: 0.00 - 0.18 (Hero)
      // Stage 1: 0.18 - 0.40 (Assembled Earbud)
      // Stage 2: 0.40 - 0.62 (Shell Separating)
      // Stage 3: 0.62 - 0.84 (Full Teardown)
      // Stage 4: 0.84 - 1.00 (Offer Bundle CTA)

      this.stages.forEach((stage, idx) => {
        let opacity = 0;
        let translateY = 20;

        if (idx === 0) {
          if (p < 0.08) {
            opacity = 1;
            translateY = 0;
          } else if (p < 0.18) {
            const fade = (p - 0.08) / 0.10;
            opacity = Math.max(0, 1 - fade);
            translateY = fade * 18;
          }
        } else if (idx === 1) {
          if (p >= 0.16 && p <= 0.40) {
            const mid = 0.28;
            const dist = Math.abs(p - mid) / 0.12;
            opacity = Math.max(0, 1 - Math.pow(dist, 1.5));
            translateY = dist * 15;
          }
        } else if (idx === 2) {
          if (p >= 0.38 && p <= 0.62) {
            const mid = 0.50;
            const dist = Math.abs(p - mid) / 0.12;
            opacity = Math.max(0, 1 - Math.pow(dist, 1.5));
            translateY = dist * 15;
          }
        } else if (idx === 3) {
          if (p >= 0.60 && p <= 0.84) {
            const mid = 0.72;
            const dist = Math.abs(p - mid) / 0.12;
            opacity = Math.max(0, 1 - Math.pow(dist, 1.5));
            translateY = dist * 15;
          }
        } else if (idx === 4) {
          if (p >= 0.82) {
            const fade = Math.min(1, (p - 0.82) / 0.10);
            opacity = fade;
            translateY = (1 - fade) * 18;
          }
        }

        stage.style.opacity = opacity.toFixed(3);
        stage.style.transform = `translateY(${translateY.toFixed(1)}px)`;
        stage.style.pointerEvents = opacity > 0.5 ? 'auto' : 'none';
      });
    }
  }

  function initScrollers() {
    document.querySelectorAll('.apple-scrollytelling-track').forEach(container => {
      new HainoTekoAir5TeardownEngine(container);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initScrollers);
  } else {
    initScrollers();
  }
})();
